import { Calendar, Flex, Button, Typography, theme } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import type { Dayjs } from "antd/node_modules/dayjs";
import dayjs from "antd/node_modules/dayjs";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { colors } from "shared/theme/color";
import { useScheduleState } from "./ScheduleFilterContext";
import { currentLang } from "i18n";
import { textPx } from "shared/constants/zoom";

// Compact square nav buttons in the mini-calendar header (prototype `.mini-nav`).
const miniNavStyle: React.CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: 7,
  padding: 0,
};

// Unscaled width the antd Calendar is laid out at before the CSS transform
// shrinks it to the sider (see the wrapper below).
const CALENDAR_LAYOUT_WIDTH = 300;
// Scale denominator: slightly wider than the layout width so the scaled
// calendar keeps a few pixels of air on its right edge.
const CALENDAR_SCALE_BASIS = 310;
// Unscaled height of the custom header + a six-row month grid, measured from
// the rendered calendar. Only the first paint uses it; the ResizeObserver
// below replaces it with the live figure.
const CALENDAR_LAYOUT_HEIGHT_FALLBACK = 345;

/**
 * Antd Calendar (https://ant.design/components/calendar) is hardcoded to fit 280-300px
 * Put the calendar in a wrapper and scale the Calendar to the desirable width
 * Customise the header to show back & forth buttons with the current month & year
 */
export const MiniSchedule = ({
  width,
  compact = false,
}: {
  width: number;
  compact?: boolean;
}) => {
  const { token } = theme.useToken();
  const { state, setView } = useScheduleState();
  const { view } = state;
  const { date } = view;
  const scale = width / CALENDAR_SCALE_BASIS;

  // A CSS transform scales the paint, not the layout, so the wrapper has to be
  // told how tall the scaled calendar is. It used to be a square (height =
  // width), which is ~25px short of a six-row month at this scale: the sixth
  // row (the 31st, or the first days of next month) rendered half-clipped
  // under the wrapper's overflow:hidden, looking "sunken". Measure the real
  // calendar instead and size the wrapper to it.
  const calendarRef = useRef<HTMLDivElement>(null);
  const [layoutHeight, setLayoutHeight] = useState(
    CALENDAR_LAYOUT_HEIGHT_FALLBACK
  );
  useLayoutEffect(() => {
    const el = calendarRef.current;
    if (!el) return;
    const measure = () => {
      const next = el.offsetHeight;
      if (next > 0) setLayoutHeight(next);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Convert the date string from state to Ant Design's Dayjs instance for controlled Calendar
  const calendarValue = useMemo(() => {
    return dayjs(date);
  }, [date]);

  const onPanelChange = (date: Dayjs) => {
    setView((prev) => ({ ...prev, date: date.format("YYYY-MM-DD") }));
  };

  const onChange = (date: Dayjs) => {
    setView((prev) => ({ ...prev, date: date.format("YYYY-MM-DD") }));
  };

  return (
    <div
      // Compact: shorter rows when the filter panel packs tighter on a short
      // window (index.css, .mini-calendar[data-compact]); the dates keep
      // their size.
      className="mini-calendar"
      data-compact={compact || undefined}
      style={{
        width: width,
        height: Math.ceil(layoutHeight * scale),
        overflow: "hidden",
        // No border: the mini-calendar sits flush inside the filter panel card.
        borderRadius: token.borderRadiusLG,
        background: colors.card,
      }}
    >
      <div
        ref={calendarRef}
        style={{
          width: CALENDAR_LAYOUT_WIDTH,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        <Calendar
          fullscreen={false}
          value={calendarValue}
          headerRender={({ value, onChange }) => {
            // The value is antd's own dayjs, on its English default; the weekday
            // row and the week title beside it already follow the language.
            const formatDate = value.locale(currentLang()).format("MMM YYYY");

            const goPrevMonth = () => {
              const prev = value.subtract(1, "month").endOf("month");
              onChange(prev);
            };

            const goNextMonth = () => {
              const nextMonthStart = value.add(1, "month").startOf("month");
              onChange(nextMonthStart);
            };

            return (
              <Flex
                align="center"
                justify="space-between"
                style={{ padding: "4px 2px 10px" }}
              >
                <Button
                  icon={<LeftOutlined />}
                  style={miniNavStyle}
                  onClick={goPrevMonth}
                />
                <Typography.Text
                  strong
                  style={{
                    fontSize: textPx(14),
                    fontFamily: "var(--font-display)",
                    color: colors.ink,
                  }}
                >
                  {formatDate}
                </Typography.Text>
                <Button
                  icon={<RightOutlined />}
                  style={miniNavStyle}
                  onClick={goNextMonth}
                />
              </Flex>
            );
          }}
          onPanelChange={onPanelChange}
          onChange={onChange}
        />
      </div>
    </div>
  );
};
